import React, { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import { getMapboxToken } from '../../utils/functions';
import coalMines from '../../../static/data/data.json';
import closingIcon from '../../../static/images/coal-closing-2030.svg';
import operationalIcon from '../../../static/images/coal-operational-after-2030.svg';
import uncertainIcon from '../../../static/images/coal-uncertain.svg';

const MAPBOX_ACCESS_TOKEN = getMapboxToken();
const SOURCE_ID = 'coal-mines';
const LAYER_ID = 'coal-mine-icons';

const STATUS = {
  A: 'Closed',
  B: 'Still operating',
  C: 'Uncertain',
};

const STATUS_COLORS = {
  A: '#D95F02',
  B: '#0072B2',
  C: '#70757A',
};

const ICONS = {
  A: { id: 'coal-closing', src: closingIcon },
  B: { id: 'coal-operational', src: operationalIcon },
  C: { id: 'coal-uncertain', src: uncertainIcon },
};

function loadImage(src) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
  });
}

function createPopupContent(properties) {
  const content = document.createElement('div');
  const name = document.createElement('strong');
  const meta = document.createElement('div');
  const icon = document.createElement('img');

  content.className = 'coal-popup';
  content.style.setProperty(
    '--project-color',
    STATUS_COLORS[properties.year] || STATUS_COLORS.C,
  );
  name.textContent = properties.Name || 'Coal mine';
  meta.className = 'coal-popup__meta';
  icon.className = 'coal-popup__icon';
  icon.src = ICONS[properties.year]?.src || ICONS.C.src;
  icon.alt = '';
  meta.append(icon);
  content.append(name, meta);

  if (properties.description?.trim()) {
    const description = document.createElement('p');
    description.className = 'map-popup__description';
    description.textContent = properties.description;
    content.append(description);
  }

  return content;
}

export default function MapCoal() {
  const mapContainer = useRef();
  const mapRef = useRef();

  useEffect(() => {
    mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    const map = new mapboxgl.Map({
      container: mapContainer.current,
      style: 'mapbox://styles/sbsonline/clxy6borh001q01oeeq147zxx',
      center: [151.1, -32.65],
      zoom: 8,
      minZoom: 6,
      maxZoom: 14,
      pitch: 0,
      bearing: 0,
      cooperativeGestures: true,
      maxBounds: [
        [149.8, -33.8],
        [152.1, -31.6],
      ],
    });
    mapRef.current = map;

    map.setProjection('mercator');
    map.addControl(
      new mapboxgl.NavigationControl({ showCompass: false }),
      'top-left',
    );
    map.dragRotate.disable();
    map.touchZoomRotate.disableRotation();
    map.scrollZoom.disable();

    map.on('load', async () => {
      const iconEntries = Object.values(ICONS);
      const images = await Promise.all(
        iconEntries.map(({ src }) => loadImage(src)),
      );

      iconEntries.forEach(({ id }, index) => {
        if (!map.hasImage(id)) {
          map.addImage(id, images[index], { pixelRatio: 1 });
        }
      });

      map.addSource(SOURCE_ID, {
        type: 'geojson',
        data: coalMines,
      });

      map.addLayer({
        id: LAYER_ID,
        type: 'symbol',
        source: SOURCE_ID,
        layout: {
          'icon-image': [
            'match',
            ['get', 'year'],
            'A',
            ICONS.A.id,
            'B',
            ICONS.B.id,
            ICONS.C.id,
          ],
          'icon-size': ['interpolate', ['linear'], ['zoom'], 6, 0.7, 10, 1],
          'icon-allow-overlap': true,
        },
      });

      const bounds = new mapboxgl.LngLatBounds();
      coalMines.features.forEach(({ geometry }) => {
        if (geometry.type === 'Point') bounds.extend(geometry.coordinates);
      });
      map.fitBounds(bounds, {
        padding: window.innerWidth > 600 ? 70 : 40,
        duration: 0,
        maxZoom: 10,
      });
    });

    map.on('click', LAYER_ID, (event) => {
      const feature = event.features && event.features[0];
      if (!feature) return;

      new mapboxgl.Popup({ offset: 24, closeButton: true })
        .setLngLat(feature.geometry.coordinates)
        .setDOMContent(createPopupContent(feature.properties))
        .addTo(map);
    });

    map.on('mouseenter', LAYER_ID, () => {
      map.getCanvas().style.cursor = 'pointer';
    });

    map.on('mouseleave', LAYER_ID, () => {
      map.getCanvas().style.cursor = '';
    });

    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, []);

  return (
    <div>
      <div className="coal-map-legend" aria-label="Coal mine status legend">
        {Object.entries(ICONS).map(([year, icon]) => (
          <div className="coal-map-legend__item" key={year}>
            <img src={icon.src} alt="" />
            <span>{STATUS[year]}</span>
          </div>
        ))}
      </div>
      <div id="map-wrap">
        <div
          ref={mapContainer}
          className="mapContainer"
          aria-label="Map of Hunter Region coal mines by operating status"
        />
      </div>
    </div>
  );
}
